import { styled } from '@mui/material/styles';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';

/**
 * Period-over-period delta pill — mirrors `.tile__delta` in
 * `design/mockups/admin/dashboard.html`. Green (up arrow) for a
 * non-negative delta, red (down arrow) for negative.
 *
 * `deltaPct === null` (prior period had zero GMV → division undefined)
 * renders a neutral "new" chip rather than a fake percentage.
 */
const Pill = styled('span')<{ tone: 'pos' | 'neg' | 'neutral' }>(({ tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  fontSize: 11,
  fontWeight: 700,
  padding: '2px 7px',
  borderRadius: 999,
  whiteSpace: 'nowrap',
  color:
    tone === 'pos'
      ? tokens.state.success
      : tone === 'neg'
        ? tokens.state.danger
        : tokens.text.tertiary,
  background:
    tone === 'pos'
      ? tokens.state.successBg
      : tone === 'neg'
        ? tokens.state.dangerBg
        : tokens.surface.muted,
}));

export const DeltaBadge = ({ deltaPct }: { deltaPct: number | null }): ReactElement => {
  const { t } = useTranslation();

  if (deltaPct === null) {
    return <Pill tone="neutral">{t('dashboard.gmv.deltaNew')}</Pill>;
  }

  const pos = deltaPct >= 0;
  return (
    <Pill tone={pos ? 'pos' : 'neg'}>
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={pos ? 'M5 15l7-7 7 7' : 'M5 9l7 7 7-7'} />
      </svg>
      {`${pos ? '+' : ''}${deltaPct.toFixed(1)}%`}
    </Pill>
  );
};
